<template>
  <div class="card listing-card"
    :class="{ 'border-success': initialPropertyLocality.primary}">
    <form @submit.prevent class="needs-validation" novalidate>
      <div v-if="serverError" class="alert alert-danger" role="alert">
        {{serverError}}
      </div>
      <div v-if="initialPropertyLocality.primary" class="card-header font-weight-bold text-success">
        Primary
      </div>
      <div class="card-body">
        <div class="row">
          <div class="col-sm-4 label-text">
            <span v-if="!propertyLocality.id">Add Locality/Tax Parcel(s)</span>
            <span v-if="propertyLocality.id">Edit Locality/Tax Parcel(s)</span>
          </div>
        </div>
        <hr>
        <div class="form-group row">
          <label for="locality" class="col-lg-4 col-form-label label-text">
            <span class="text-danger">*</span>Locality Name
          </label>
          <div class="loading col-lg-5" v-if="localityOptionsLoading">
            Loading...
          </div>
          <div class="col-lg-8">
            <div v-if="propertyLocality.id && !localityOptionsLoading">
              {{getLocalityName(propertyLocality.localityId)}}
            </div>
            <select v-if="!propertyLocality.id && !localityOptionsLoading"
                    v-validate="'required'"
                    data-vv-as="Locality"
                    class="form-control"
                    name="locality"
                    v-model="propertyLocality.localityId">
              <option value="" disabled>Select Locality</option>
              <option v-for="option in unselectedLocalities"
                      :key="option.id"
                      :value="option.id">
              {{ option.name }}
              </option>
            </select>
            <div class="invalid-feedback">{{ errors.first('locality') }}</div>
          </div>
        </div>
        <div class="form-group row">
          <div class="col-md-4 label-text">Tax Parcel(s)</div>
          <div v-if="!propertyLocality.taxParcels || propertyLocality.taxParcels.length === 0"
               class="col-md-8">
            None
          </div>
          <div class="col-md-8">
            <ul class="list-group">
              <li v-for='(taxParcel, index) in propertyLocality.taxParcels'
                :key='index'
                class="list-group-item">
                <div class="form-row">
                  <div class="form-group col-md-6">
                    <div class="label-text">
                      Tax Parcel ID
                    </div>
                    <input data-vv-as="Tax Parcel ID"
                           type="text"
                           class="form-control"
                           name="parcelId"
                           v-model="taxParcel.parcelId"
                           placeholder="Enter Tax Parcel ID"
                           maxlength="35" />
                    <small class="form-text text-muted">
                      Max 35 characters
                    </small>
                  </div>
                  <div class="form-group col-md-5">
                    <div class="label-text">
                      Tax Parcel Acres
                    </div>
                    <input data-vv-as="Tax Parcel Acres"
                           class="form-control"
                           name="acres"
                           v-model="taxParcel.acres"
                           placeholder="Enter Tax Parcel Acres"
                           v-validate="'max_decimal:4|min_value:0.0001'" />
                    <div class="invalid-feedback">{{ errors.first('acres') }}</div>
                  </div>
                  <div class="form-group col-md-1">
                    <div class="label-text d-none d-md-block">
                      &nbsp;
                    </div>
                    <a class="form-action-icon text-danger"
                      :disabled="submitting"
                      @click='removeTaxParcel(index)'>
                      <font-awesome-icon icon='trash-alt' />
                    </a>
                  </div>
                </div>
              </li>
            </ul>
          </div>
        </div>
        <a class="form-action-text text-secondary offset-md-4 white-space-nowrap"
          :disabled="submitting"
          @click="addTaxParcel()">
          + Add Tax Parcel
        </a>
      </div>
      <div class='card-footer right-align-content'>
        <b-button :disabled="submitting" variant="danger" class="button-row-spacing"
                  v-b-modal="'cancelConfirmation-' + propertyLocality.id">
          Cancel
        </b-button>
        <b-button :disabled="submitting"
          @click="submitWrapper(propertyLocality)">
          Save
        </b-button>
      </div>
    </form>
    <CancelModal
      :modalId="'cancelConfirmation-' + propertyLocality.id"
      @cancelled="cancelEdit">
    </CancelModal>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import CancelModal from '../CancelModal.vue';
import FormSubmit from '../../mixins/FormSubmit';
import { EntityModules, LookupModules } from '@/store/moduleConfig';

export default {
  name: 'LocalityForm',
  components: {
    CancelModal,
  },
  mixins: [FormSubmit],
  props: [
    'initialPropertyLocality',
    'taxParcelSort',
  ],
  data() {
    return {
      propertyLocality: {
        id: this.initialPropertyLocality.id,
        propertyId: this.initialPropertyLocality.propertyId,
        primary: this.initialPropertyLocality.primary,
        localityId: this.initialPropertyLocality.localityId,
        taxParcels: this.initialPropertyLocality.taxParcels
          .map(tp => ({ ...tp }))
          .sort((a, b) => (this.taxParcelSort(a, b))),
      },
    };
  },
  computed: {
    ...mapState(LookupModules.LOCALITIES.namespace, {
      localityOptionsLoading: state => state.loading,
    }),
    ...mapState(EntityModules.LOCALITY.namespace, {
      entities: state => state.entities,
      serverError: state => state.serverError,
      submitting: state => state.submitting,
    }),
    ...mapGetters(LookupModules.LOCALITIES.namespace, {
      getLocalityName: 'getName',
      localityOptions: 'getActiveOptions',
    }),
    unselectedLocalities() {
      return this.localityOptions.filter(option => this.entities && !this.entities
        .some(locality => locality.localityId === option.id));
    },
  },
  methods: {
    addTaxParcel() {
      this.propertyLocality.taxParcels.push({ id: null, parcelId: '', acres: '' });
    },
    removeTaxParcel(index) {
      this.propertyLocality.taxParcels.splice(index, 1);
    },
    submitWrapper(entity) {
      // Set the local state of primary to the prop state in case it has changed
      this.propertyLocality.primary = this.initialPropertyLocality.primary;
      // remove tax parcels where no data has been entered
      this.propertyLocality.taxParcels = this.propertyLocality.taxParcels
        .filter(taxParcel => taxParcel.parcelId || taxParcel.acres);

      this.onSubmit(
        entity,
        EntityModules.LOCALITY.namespace,
      );
    },
    cancelEdit() {
      this.$emit('closeForm', this.propertyLocality.id);
    },
  },
};
</script>
